<template>
  <div class="flex flex-col gap-2">
    <label v-if="label" class="text-sm font-medium text-obsidian-800">{{ label }}</label>
    <FileUpload
      v-model="selectedFile"
      :hint="error"
      :is-error="!!error"
      :accept="accept"
      class="w-full"
    />
  </div>
</template>

<script lang="ts" setup>
import FileUpload from '@/components/FileInput.vue';
import { mediaService } from '@/api/media.service';
import { extractFileNameFromFile } from '@/utils/extract-file-name';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = withDefaults(
  defineProps<{
    label?: string;
    accept?: string;
  }>(),
  {
    accept: 'video/*',
  },
);

const { t } = useI18n();

const selectedFile = ref<File | null>(null);
const error = ref<string>();
const isUploading = ref(false);

const upload = async () => {
  if (!selectedFile.value) {
    error.value = t('fileRequired');
    throw new Error('File required');
  }

  error.value = undefined;
  isUploading.value = true;
  try {
    const { fullFileName } = extractFileNameFromFile(selectedFile.value);
    return await mediaService.uploadVideoFile(fullFileName, selectedFile.value);
  } finally {
    isUploading.value = false;
  }
};

const reset = () => {
  selectedFile.value = null;
  error.value = undefined;
};

defineExpose({ upload, reset, selectedFile, isUploading });
</script>

<i18n lang="json">
{
  "en": {
    "fileRequired": "Please select a video file."
  },
  "de": {
    "fileRequired": "Bitte eine Videodatei auswählen."
  },
  "es": {
    "fileRequired": "Seleccione un archivo de vídeo."
  }
}
</i18n>
